import React from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { replyToCondition, setConditionKeywords, useConditions } from '../../src/data';
import { Action, Label, Panel } from '../../src/ui';
import { D } from '../../src/theme';

/**
 * 조건이 어떤 제품을 잡고 있는지 보여 주고 사용자가 직접 고르는 화면.
 *
 * AI 는 "오쏘몰 같은 영양제" 를 들어도 경쟁 제품을 다 떠올리지 못한다. 반대로 그 제품을 쓰는 사람은
 * 안다. 그래서 등록 직후 한 번 확인받고, 그 뒤로는 언제든 여기서 고칠 수 있게 한다.
 * 사용자가 고른 목록이 항상 AI 추측보다 우선한다.
 */
export default function ConditionScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const conds = useConditions();
  const cond = conds.find((c) => c.id === id);
  // 화면이 막 열린 순간에는 목록이 아직 안 왔다. 그때 "없어요" 라고 하면
  // 사용자는 조건이 사라진 줄 알고, 뒤로 갈 방법도 없다고 느낀다.
  const loading = conds.length === 0;

  const [picked, setPicked] = React.useState<string[] | null>(null);
  const [draft, setDraft] = React.useState('');
  const [reply, setReply] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [extra, setExtra] = React.useState<string[]>([]);
  const [saved, setSaved] = React.useState(false);

  const candidates = React.useMemo(() => cond?.candidates ?? [], [cond]);
  // 대화가 없어도 첫 질문이 있으면 그걸 첫 마디로 보여 준다
  const turns = React.useMemo(() => {
    const c = cond?.chat ?? [];
    if (c.length) return c;
    return cond?.needs_confirm && cond?.question ? [{ role: 'ai' as const, text: cond.question }] : [];
  }, [cond]);
  const waiting = turns.length > 0 && turns[turns.length - 1].role === 'user';
  // 처음 들어오면 AI 가 고른 것이 전부 켜져 있는 상태에서 시작한다. 사용자는 빼기만 하면 된다
  const selected = picked ?? candidates.map((c) => c.k);
  const all = [...candidates, ...extra.map((k) => ({ k, example: null }))];

  if (!cond) {
    return (
      <View style={st.center}>
        {loading ? (
          <ActivityIndicator size="small" color={D.ink} />
        ) : (
          <>
            <Text style={{ fontSize: 15, fontWeight: '700', color: D.ink2 }}>이 조건은 더 이상 없어요</Text>
            <Pressable onPress={() => router.replace('/(tabs)/conditions')} style={st.ghost}>
              <Text style={st.ghostText}>내 조건으로</Text>
            </Pressable>
          </>
        )}
      </View>
    );
  }

  function toggle(k: string) {
    setSaved(false);
    setPicked(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  }

  function addDraft() {
    const k = draft.trim();
    if (!k || all.some((c) => c.k === k)) return setDraft('');
    setExtra([...extra, k]);
    setPicked([...selected, k]);
    setDraft('');
    setSaved(false);
  }

  async function send() {
    const t = reply.trim();
    if (!t || sending) return;
    setSending(true);
    setReply('');
    await replyToCondition(cond!.id, t, turns).catch(() => {});
    setSending(false);
  }

  async function save() {
    await setConditionKeywords(cond!.id, selected).catch(() => {});
    setSaved(true);
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: D.ground }}
      contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 12, paddingBottom: insets.bottom + 32, gap: 10 }}
    >
      <Panel style={{ gap: 6 }}>
        <Text style={st.head}>{cond.text}</Text>
        {cond.parsed ? <Text style={st.headSub}>{cond.parsed}</Text> : null}
      </Panel>

      {turns.length ? (
        <Panel style={{ gap: 10 }}>
          <Label>AI 와 좁히기</Label>
          {turns.map((t, i) => (
            <View key={i} style={[st.bubble, t.role === 'user' ? st.mine : st.theirs]}>
              <Text style={[st.bubbleText, t.role === 'user' && { color: D.volt }]}>{t.text}</Text>
            </View>
          ))}
          {waiting ? <Text style={st.hint}>AI 가 답하는 중이에요…</Text> : null}
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <TextInput
              value={reply}
              onChangeText={setReply}
              placeholder="예: 오쏘몰 이뮨 같은 거요"
              placeholderTextColor={D.ink3}
              returnKeyType="send"
              onSubmitEditing={send}
              style={st.input}
            />
            <Pressable onPress={send} disabled={!reply.trim() || sending} style={[st.send, (!reply.trim() || sending) && { opacity: 0.35 }]}>
              <Text style={st.sendText}>보내기</Text>
            </Pressable>
          </View>
          <Text style={st.hint}>
            제품 이름을 하나만 대 주셔도 비슷한 제품까지 같이 찾아 등록해요.
            {cond.needs_confirm ? ' 아직 상품을 못 좁혀서, 확인을 마쳐야 알림이 가요.' : ''}
          </Text>
        </Panel>
      ) : null}

      <Panel style={{ gap: 10 }}>
        <Label>이 조건이 찾는 제품</Label>
        {all.length === 0 ? (
          <Text style={st.hint}>
            아직 제품 목록이 없어요. 수집기가 문장을 해석하는 중이거나(5분 이내), 상품명이 하나로 정해진 조건이에요.
          </Text>
        ) : (
          <View>
            {all.map((c, i) => {
              const on = selected.includes(c.k);
              return (
                <Pressable
                  key={c.k}
                  onPress={() => toggle(c.k)}
                  style={[st.row, i < all.length - 1 && { borderBottomWidth: 1, borderBottomColor: D.line }]}
                >
                  <View style={[st.box, on && { backgroundColor: D.ink }]}>
                    {on ? <Text style={st.check}>✓</Text> : null}
                  </View>
                  <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
                    <Text style={[st.rowName, !on && { color: D.ink3 }]}>{c.k}</Text>
                    <Text style={[st.rowEx, !on && { color: D.ink3 }]} numberOfLines={1}>
                      {c.example ? `예: ${c.example}` : '아직 이 말이 들어간 딜은 못 봤어요'}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        )}
        <Text style={st.hint}>
          하나라도 제목에 들어 있으면 알림 후보가 돼요. 엉뚱한 예시가 보이면 그 줄을 꺼 주세요.
          켜 둔 것 중에서 진짜 찾던 물건인지는 알림 전에 AI 가 한 번 더 봅니다.
        </Text>
      </Panel>

      <Panel style={{ gap: 10 }}>
        <Label>빠진 제품 추가</Label>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={addDraft}
            placeholder="예: 오쏘몰 이뮨"
            placeholderTextColor={D.ink3}
            returnKeyType="done"
            style={st.input}
          />
          <Pressable onPress={addDraft} disabled={!draft.trim()} style={[st.send, !draft.trim() && { opacity: 0.35 }]}>
            <Text style={st.sendText}>추가</Text>
          </Pressable>
        </View>
        <Text style={st.hint}>제품 이름을 아는 게 AI 보다 정확해요. 쓰시는 제품이나 비슷한 제품을 직접 넣어 주세요.</Text>
      </Panel>

      <Action label={saved ? '저장했어요' : `${selected.length}개로 저장`} onPress={save} disabled={saved} />
      <Text style={st.hint}>저장하면 다음 수집(5분 이내)부터 이 목록으로 찾습니다.</Text>
    </ScrollView>
  );
}

const st = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 14, backgroundColor: D.ground },
  ghost: { borderWidth: 1, borderColor: D.ink, backgroundColor: D.panel, paddingVertical: 5, paddingHorizontal: 10 },
  ghostText: { fontSize: 12, fontWeight: '900', color: D.ink },

  head: { fontSize: 16.5, fontWeight: '800', color: D.ink, lineHeight: 23, letterSpacing: -0.3 },
  headSub: { fontSize: 12.5, color: D.ink2, lineHeight: 18 },

  bubble: { maxWidth: '88%', paddingVertical: 9, paddingHorizontal: 11, borderWidth: 1, borderColor: D.ink },
  theirs: { alignSelf: 'flex-start', backgroundColor: D.panel },
  mine: { alignSelf: 'flex-end', backgroundColor: D.ink },
  bubbleText: { fontSize: 13.5, lineHeight: 20, color: D.ink, fontWeight: '600' },

  input: { flex: 1, height: 42, borderWidth: 1, borderColor: D.ink, backgroundColor: D.panel, paddingHorizontal: 11, fontSize: 14, color: D.ink },
  send: { height: 42, paddingHorizontal: 14, backgroundColor: D.ink, alignItems: 'center', justifyContent: 'center' },
  sendText: { fontSize: 13, fontWeight: '900', color: D.volt },

  row: { flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 11 },
  box: { width: 20, height: 20, borderWidth: 1, borderColor: D.ink, backgroundColor: D.panel, alignItems: 'center', justifyContent: 'center' },
  check: { fontSize: 12, fontWeight: '900', color: D.volt, lineHeight: 14 },
  rowName: { fontSize: 14.5, fontWeight: '800', color: D.ink },
  rowEx: { fontSize: 11.5, color: D.ink2 },

  hint: { fontSize: 11.5, color: D.ink2, lineHeight: 17 },
});
